import React, { useEffect, forwardRef, useState } from 'react';
import { ARButton } from '@react-three/xr';
import { useSnapshot } from 'valtio';

import { state, toggleDomOverlay } from '../state';

export const DOMOverlay = forwardRef(function DOMOverlay(props, ref) {
  const { showDomOverlay, runFallback, showInfoPanel, hasInteracted } = useSnapshot(state);

  const [data, setData] = useState({});
  useEffect(() => {
    if (!window.tooltipData) return;
    setData(window.tooltipData);
  }, [setData]);

  useEffect(() => {
    if (runFallback) {
      toggleDomOverlay(true);
    }
  }, [runFallback]);

  // toggle Overlay on ESC
  useEffect(() => {
    function escFunction(event) {
      if (event.key === 'Escape') {
        toggleDomOverlay();
      }
    }
    document.addEventListener('keydown', escFunction);
    return () => document.removeEventListener('keydown', escFunction);
  }, []);

  return (
    <div
      ref={ref}
      id="XrDomOverlay"
      className={runFallback ? 'fallback' : ''}
      onTouchEnd={() => {
        if (!state.showInfoPanel) {
          toggleDomOverlay();
        }
      }}
      style={{
        opacity: showDomOverlay ? 1 : 0,
      }}>
      <div className="XrDomOverlay-content">
        <ARButton
          exitOnly
          style={{}} // Remove default styling
          className="XrDomOverlay-btnExit hp__cta"
          onClick={() => {
            if (runFallback) {
              // fallback Desktop
              state.runFallback = false;
            }
          }}
          onTouchEnd={e => {
            if (runFallback) {
              // fallback Phone
              state.runFallback = false;
              // prevent parent from toggling
              e.preventDefault();
              e.stopPropagation();
            }
          }}>
          <svg
            width="15"
            height="16"
            viewBox="0 0 15 16"
            fill="black"
            xmlns="http://www.w3.org/2000/svg">
            <path d="M1.18784 15.5L0 14.3122L6.31216 8L0 1.68784L1.18784 0.5L7.5 6.81216L13.8122 0.5L15 1.68784L8.68784 8L15 14.3122L13.8122 15.5L7.5 9.18784L1.18784 15.5Z" />
          </svg>
        </ARButton>
        <button
          className="XrDomOverlay-btnInfo hp__cta"
          onClick={() => {
            // open info panel
            state.showInfoPanel = true;
            // make sure dom overlay wasn't about to hide
            state.showDomOverlay = true;
          }}
          onTouchEnd={e => {
            // prevent parent from toggling
            e.stopPropagation();
          }}>
          <svg width="19" height="20" viewBox="0 0 19 20" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M17.4426 10C17.4426 14.3866 13.8866 17.9426 9.5 17.9426C5.11341 17.9426 1.55738 14.3866 1.55738 10C1.55738 5.61341 5.11341 2.05738 9.5 2.05738C13.8866 2.05738 17.4426 5.61341 17.4426 10ZM19 10C19 15.2467 14.7467 19.5 9.5 19.5C4.25329 19.5 0 15.2467 0 10C0 4.75329 4.25329 0.5 9.5 0.5C14.7467 0.5 19 4.75329 19 10ZM10.2962 8.31632V14.8277H8.56099V8.31632H10.2962ZM10.0944 6.11518C9.91525 6.29052 9.69333 6.37819 9.42861 6.37819C9.16389 6.37819 8.94197 6.29052 8.76285 6.11518C8.58374 5.93985 8.49418 5.72261 8.49418 5.46347C8.49418 5.20434 8.58374 4.9871 8.76285 4.81177C8.94197 4.63662 9.16389 4.54905 9.42861 4.54905C9.69333 4.54905 9.91525 4.63662 10.0944 4.81177C10.2735 4.9871 10.363 5.20434 10.363 5.46347C10.363 5.72261 10.2735 5.93985 10.0944 6.11518Z"
            />
          </svg>
          {data.title}
        </button>
        <div className={`XrDomOverlay-moveTooltip ${hasInteracted ? 'hasMoved' : ''}`}>
          {data.tooltip}
        </div>
        <div className="XrDomOverlay-infoPanel" style={{ display: showInfoPanel ? '' : 'none' }}>
          <svg
            className="XrDomOverlay-infoPanel__icon"
            width="19"
            height="20"
            viewBox="0 0 19 20"
            xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M17.4426 10C17.4426 14.3866 13.8866 17.9426 9.5 17.9426C5.11341 17.9426 1.55738 14.3866 1.55738 10C1.55738 5.61341 5.11341 2.05738 9.5 2.05738C13.8866 2.05738 17.4426 5.61341 17.4426 10ZM19 10C19 15.2467 14.7467 19.5 9.5 19.5C4.25329 19.5 0 15.2467 0 10C0 4.75329 4.25329 0.5 9.5 0.5C14.7467 0.5 19 4.75329 19 10ZM10.2962 8.31632V14.8277H8.56099V8.31632H10.2962ZM10.0944 6.11518C9.91525 6.29052 9.69333 6.37819 9.42861 6.37819C9.16389 6.37819 8.94197 6.29052 8.76285 6.11518C8.58374 5.93985 8.49418 5.72261 8.49418 5.46347C8.49418 5.20434 8.58374 4.9871 8.76285 4.81177C8.94197 4.63662 9.16389 4.54905 9.42861 4.54905C9.69333 4.54905 9.91525 4.63662 10.0944 4.81177C10.2735 4.9871 10.363 5.20434 10.363 5.46347C10.363 5.72261 10.2735 5.93985 10.0944 6.11518Z"
            />
          </svg>
          <div>
            <h2>{data.title}</h2>
            <svg
              className="XrDomOverlay-infoPanel__btnClose"
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="black"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => (state.showInfoPanel = false)}>
              <path d="M1.18784 15.5L0 14.3122L6.31216 8L0 1.68784L1.18784 0.5L7.5 6.81216L13.8122 0.5L15 1.68784L8.68784 8L15 14.3122L13.8122 15.5L7.5 9.18784L1.18784 15.5Z" />
            </svg>
            <p>{data.text}</p>
            <a
              href={data.internal_page}
              onClick={() => {
                // make sure document is scrollable again
                // React doesnt fire unmount when changing URL abruptly
                document.documentElement.style.overflow = '';
              }}>
              {data.label}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
});
